import { createContext, useState, useMemo, useContext, useCallback, useEffect, useRef } from 'react';

const CalidadContext = createContext();

const API = process.env.REACT_APP_SERVICE_ENDPOINT_NE;

// ── Helpers ──────────────────────────────────────────────────────────────
function getToken() {
    try {
        const raw = sessionStorage.getItem('token');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.token || parsed;
    } catch {
        return null;
    }
}

async function parseErrorMessage(res, fallback) {
    try {
        const text = await res.text();
        const json = JSON.parse(text);
        if (json.detail) return json.detail;
        if (json.title) return json.title;
        return fallback;
    } catch {
        return fallback;
    }
}

const ERROR_MESSAGES = {
    400: 'Los datos enviados no son válidos. Revisa los campos e intenta de nuevo.',
    401: 'Tu sesión ha expirado. Vuelve a iniciar sesión.',
    403: 'No tienes permiso para realizar esta acción.',
    404: 'No se encontró el registro solicitado.',
    409: 'Conflicto: el registro ya fue modificado por otro usuario.',
    500: 'Error interno del servidor. Intenta de nuevo más tarde.',
};

function getFriendlyError(status, serverDetail) {
    if (serverDetail && status !== 500) return serverDetail;
    return ERROR_MESSAGES[status] || 'Ocurrió un error inesperado. Intenta de nuevo.';
}

const FETCH_TIMEOUT_MS = 15000;

async function apiFetch(path, options = {}) {
    const token = getToken();
    if (!token) throw new Error('Sin token de sesión');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
        const res = await fetch(`${API}${path}`, {
            ...options,
            signal: controller.signal,
            headers: {
                authorization: `Bearer ${token}`,
                Accept: 'application/json',
                'Content-Type': 'application/json',
                ...options.headers,
            },
        });
        return res;
    } catch (err) {
        if (err.name === 'AbortError') {
            throw new Error('El servidor no respondió. Verifica tu conexión e intenta de nuevo.');
        }
        throw err;
    } finally {
        clearTimeout(timeoutId);
    }
}

// ── Provider ─────────────────────────────────────────────────────────────
export function CalidadProvider(props) {
    const [selectedFecha, setSelectedFecha] = useState(new Date());

    // Área → Línea → Corrida
    const [areas, setAreas] = useState([]);
    const [selectedArea, setSelectedAreaState] = useState(null);
    const [lineas, setLineas] = useState([]);
    const [selectedLinea, setSelectedLineaState] = useState(null);
    const [corridas, setCorridas] = useState([]);
    const [selectedCorrida, setSelectedCorridaState] = useState(null);
    const [loadingCorridas, setLoadingCorridas] = useState(false);

    // ── Catálogos dinámicos ──────────────────────────────────────────────
    const [catalogoDefectos, setCatalogoDefectos] = useState([]);
    const [catalogoCausas, setCatalogoCausas] = useState([]);

    const [registros, setRegistros] = useState([]);
    const [loadingRegistros, setLoadingRegistros] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [errorGuardado, setErrorGuardado] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);

    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const corridaId = useMemo(
        () => selectedCorrida?.corrida ?? selectedCorrida?.id ?? null,
        [selectedCorrida]
    );

    // ── Cargar áreas y catálogos al montar ────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        async function fetchAreas() {
            try {
                const res = await apiFetch('/areas');
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled) setAreas(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Error cargando áreas:', err);
            }
        }
        async function fetchCatalogos() {
            try {
                const [resDefectos, resCausas] = await Promise.all([
                    apiFetch('/MicroServiciosController?categoria=DefectosCalidad'),
                    apiFetch('/MicroServiciosController?categoria=CausaRaizCalidad'),
                ]);
                if (!cancelled && resDefectos.ok) {
                    const data = await resDefectos.json();
                    setCatalogoDefectos(
                        Array.isArray(data)
                            ? data.map((d) => ({ codigo: d.codigo, label: d.nombre }))
                            : []
                    );
                }
                if (!cancelled && resCausas.ok) {
                    const data = await resCausas.json();
                    setCatalogoCausas(
                        Array.isArray(data)
                            ? data.map((c) => ({ codigo: c.codigo, label: c.nombre }))
                            : []
                    );
                }
            } catch (err) {
                console.error('Error cargando catálogos:', err);
            }
        }
        fetchAreas();
        fetchCatalogos();
        return () => { cancelled = true; };
    }, []);

    // ── Cargar líneas cuando cambia área ─────────────────────────────────
    useEffect(() => {
        if (!selectedArea) {
            setLineas([]);
            return;
        }
        let cancelled = false;
        async function fetchLineas() {
            try {
                const res = await apiFetch(
                    `/lineas?area=${encodeURIComponent(selectedArea.area)}`
                );
                if (!res.ok) {
                    if (!cancelled) setLineas([]);
                    return;
                }
                const data = await res.json();
                if (!cancelled) setLineas(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Error cargando líneas:', err);
                if (!cancelled) setLineas([]);
            }
        }
        fetchLineas();
        return () => { cancelled = true; };
    }, [selectedArea]);

    // ── Cargar corridas cuando cambia línea o fecha ────────────────────
    useEffect(() => {
        if (!selectedLinea) {
            setCorridas([]);
            return;
        }
        let cancelled = false;
        async function fetchCorridas() {
            setLoadingCorridas(true);
            try {
                const midnightLocal = new Date(
                    selectedFecha.getFullYear(),
                    selectedFecha.getMonth(),
                    selectedFecha.getDate()
                );
                const fechaUtc = encodeURIComponent(midnightLocal.toISOString());
                const res = await apiFetch(
                    `/Corridas/GetCorridasByFecha?linea=${encodeURIComponent(selectedLinea.id)}&fecha=${fechaUtc}`
                );
                if (!res.ok) {
                    if (!cancelled) setCorridas([]);
                    return;
                }
                const data = await res.json();
                if (!cancelled) {
                    setCorridas(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                console.error('Error cargando corridas:', err);
                if (!cancelled) setCorridas([]);
            } finally {
                if (!cancelled) setLoadingCorridas(false);
            }
        }
        fetchCorridas();
        return () => { cancelled = true; };
    }, [selectedLinea, selectedFecha]);

    // ── Cargar registros de calidad cuando cambia corrida ────────────────
    const loadRegistros = useCallback(async (cId) => {
        if (!cId) { setRegistros([]); return; }
        setLoadingRegistros(true);
        try {
            const res = await apiFetch(`/Calidad?corrida=${encodeURIComponent(cId)}`);
            if (!res.ok) {
                if (mounted.current) setRegistros([]);
                return;
            }
            const data = await res.json();
            if (mounted.current) setRegistros(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error cargando registros de calidad:', err);
            if (mounted.current) setRegistros([]);
        } finally {
            if (mounted.current) setLoadingRegistros(false);
        }
    }, []);

    // ── Setters con cascada de limpieza ──────────────────────────────────
    const setSelectedArea = useCallback((area) => {
        setSelectedAreaState(area);
        setSelectedLineaState(null);
        setLineas([]);
        setSelectedCorridaState(null);
        setCorridas([]);
        setRegistros([]);
    }, []);

    const setSelectedLinea = useCallback((linea) => {
        setSelectedLineaState(linea);
        setSelectedCorridaState(null);
        setCorridas([]);
        setRegistros([]);
    }, []);

    const setSelectedCorrida = useCallback((corrida) => {
        setSelectedCorridaState(corrida);
        if (corrida) {
            loadRegistros(corrida.corrida ?? corrida.id);
        } else {
            setRegistros([]);
        }
    }, [loadRegistros]);

    // ── CRUD ─────────────────────────────────────────────────────────────
    const abrirNuevo = useCallback(() => {
        setEditTarget(null);
        setErrorGuardado(null);
        setFormOpen(true);
    }, []);

    const abrirEditar = useCallback((registro) => {
        setEditTarget(registro);
        setErrorGuardado(null);
        setFormOpen(true);
    }, []);

    const cerrarForm = useCallback(() => {
        setFormOpen(false);
        setEditTarget(null);
        setErrorGuardado(null);
    }, []);

    const guardarRegistro = useCallback(async (formData) => {
        setGuardando(true);
        setErrorGuardado(null);
        try {
            let res;
            if (editTarget) {
                res = await apiFetch(`/Calidad/${editTarget.folio}`, {
                    method: 'PUT',
                    body: JSON.stringify({
                        defecto: formData.defecto,
                        cantidad: formData.cantidad,
                        loteoSerie: formData.loteoSerie,
                        causaRaiz: formData.causaRaiz,
                        notas: formData.notas,
                    }),
                });
            } else {
                res = await apiFetch('/Calidad', {
                    method: 'POST',
                    body: JSON.stringify({
                        corrida: corridaId,
                        linea: selectedLinea?.id,
                        defecto: formData.defecto,
                        cantidad: formData.cantidad,
                        loteoSerie: formData.loteoSerie,
                        causaRaiz: formData.causaRaiz,
                        notas: formData.notas,
                    }),
                });
            }
            if (!res.ok) {
                const fallback = editTarget
                    ? 'No se pudo actualizar el registro.'
                    : 'No se pudo registrar la pieza.';
                const detail = await parseErrorMessage(res, fallback);
                throw new Error(getFriendlyError(res.status, detail));
            }
            // Recargar registros
            await loadRegistros(corridaId);
            setFormOpen(false);
            setEditTarget(null);
        } catch (err) {
            setErrorGuardado(err.message || 'Error al guardar. Intenta de nuevo.');
        } finally {
            if (mounted.current) setGuardando(false);
        }
    }, [editTarget, corridaId, selectedLinea, loadRegistros]);

    const eliminarRegistro = useCallback(async (folio) => {
        try {
            const res = await apiFetch(`/Calidad/${encodeURIComponent(folio)}`, { method: 'DELETE' });
            if (!res.ok) {
                const detail = await parseErrorMessage(res, 'No se pudo eliminar el registro.');
                throw new Error(getFriendlyError(res.status, detail));
            }
            await loadRegistros(corridaId);
        } catch (err) {
            console.error('Error eliminando registro:', err);
            setErrorGuardado(err.message || 'No se pudo eliminar el registro.');
        }
    }, [corridaId, loadRegistros]);

    // ── Resumen calculado ────────────────────────────────────────────────
    const resumen = useMemo(() => {
        const total = registros.reduce((acc, r) => acc + (r.cantidad || 1), 0);
        const defectoIds = new Set(registros.map((r) => r.defecto));
        const unicos = defectoIds.size;

        const counts = {};
        registros.forEach((r) => {
            const defObj = catalogoDefectos.find((d) => d.codigo === r.defecto);
            const label = defObj ? defObj.label : `Defecto ${r.defecto}`;
            counts[label] = (counts[label] || 0) + (r.cantidad || 1);
        });
        let defectoLider = '—';
        let maxCount = 0;
        Object.entries(counts).forEach(([label, count]) => {
            if (count > maxCount) { maxCount = count; defectoLider = label; }
        });
        return { total, unicos, defectoLider };
    }, [registros, catalogoDefectos]);

    const value = useMemo(() => ({
        selectedFecha, setSelectedFecha,
        areas,
        lineas,
        corridas,
        loadingCorridas,
        selectedArea, setSelectedArea,
        selectedLinea, setSelectedLinea,
        selectedCorrida, setSelectedCorrida,
        catalogoDefectos,
        catalogoCausas,
        registros,
        loadingRegistros,
        resumen,
        formOpen,
        editTarget,
        guardando,
        errorGuardado,
        abrirNuevo,
        abrirEditar,
        cerrarForm,
        guardarRegistro,
        eliminarRegistro,
    }), [
        selectedFecha,
        areas,
        lineas,
        corridas,
        loadingCorridas,
        selectedArea, setSelectedArea,
        selectedLinea, setSelectedLinea,
        selectedCorrida, setSelectedCorrida,
        catalogoDefectos,
        catalogoCausas,
        registros,
        loadingRegistros,
        resumen,
        formOpen,
        editTarget,
        guardando,
        errorGuardado,
        abrirNuevo,
        abrirEditar,
        cerrarForm,
        guardarRegistro,
        eliminarRegistro,
    ]);

    return <CalidadContext.Provider value={value} {...props} />;
}

export function useCalidad() {
    const context = useContext(CalidadContext);
    if (!context) throw new Error('useCalidad debe estar dentro del proveedor CalidadContext');
    return context;
}
